import React from 'react';
import { CheckCircle2, Clock, TrendingDown } from 'lucide-react';
import { Button } from './ui/button';
import mockData from '../mock';

const SubsidySection = ({ onCtaClick }) => {
  const { subsidy } = mockData;

  return (
    <section id="subsidy" className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-orange-500/20 backdrop-blur-sm border border-orange-400 rounded-full px-6 py-3 mb-6 animate-pulse-subtle">
            <Clock className="w-5 h-5 text-orange-400" />
            <span className="text-orange-300 font-bold">{subsidy.urgency}</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {subsidy.title}
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {subsidy.description}
          </p>
        </div>

        {/* Subsidy Plans */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {subsidy.plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 card-hover ${
                plan.popular ? 'border-orange-400 scale-105' : 'border-white/20'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-6 py-1 rounded-full text-sm font-bold shadow-lg">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-5xl font-bold mb-2">{plan.capacity}</h3>
                <p className="text-blue-200">{plan.idealFor}</p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center pb-3 border-b border-white/20">
                  <span className="text-blue-200">Total Cost</span>
                  <span className="font-bold text-xl">{plan.totalCost}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-green-400">
                  <span className="text-green-300 font-semibold">Subsidy</span>
                  <span className="font-bold text-2xl text-green-400">- {plan.subsidy}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white font-semibold">You Pay</span>
                  <span className="font-bold text-3xl text-orange-400">{plan.afterSubsidy}</span>
                </div>
              </div>

              <div className="bg-orange-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-orange-400" />
                  <span className="text-sm text-orange-300 font-semibold">Monthly Savings</span>
                </div>
                <p className="text-2xl font-bold">{plan.monthlySaving}</p>
              </div>

              <Button
                onClick={onCtaClick}
                className={`w-full py-6 text-lg font-semibold rounded-xl ${
                  plan.popular
                    ? 'bg-gradient-to-r from-orange-500 to-yellow-400 text-white hover:shadow-2xl'
                    : 'bg-white text-blue-900 hover:bg-blue-50'
                }`}
              >
                Get This Plan
              </Button>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
          <h3 className="text-2xl font-bold mb-6 text-center">What's Included</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              'Solar Panels',
              'Solar Inverter',
              'BOS Kit',
              '5 Year Maintenance',
              '1 Year Insurance',
              '25 Year Warranty',
              'Subsidy Paperwork',
              'Net Metering Support',
              'Monitoring System',
              'Lifetime Support'
            ].map((feature, index) => (
              <div key={index} className="flex items-center space-x-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-blue-100">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubsidySection;